<template>
  <BaseModal
    v-model="isVisible"
    :title="`Editar Ticket #${ticket?.id || ''}`"
    size="xl"
    :closable="true"
    :close-on-overlay="false"
    :persistent="hasUnsavedChanges"
    data-testid="ticket-modal"
    @close="handleClose"
  >
    <form @submit.prevent="handleSave" class="space-y-6">
      <!-- Título del Ticket -->
      <div>
        <label for="title" class="block text-sm font-medium text-gray-700 mb-2">
          Título del Ticket *
        </label>
        <InputField
          id="title"
          v-model="form.title"
          type="text"
          placeholder="Ingrese el título del ticket"
          :is-valid="titleIsValid"
          :validation-message="titleValidationMessage"
          required
        />
      </div>

      <!-- Descripción -->
      <div>
        <label for="description" class="block text-sm font-medium text-gray-700 mb-2">
          Descripción *
          <span class="text-xs text-gray-500 font-normal">({{ form.description.length }}/2000 caracteres)</span>
        </label>
        <textarea
          id="description"
          v-model="form.description"
          rows="4"
          class="block w-full px-4 py-3 text-base border rounded-xl font-sans transition-all duration-200 focus:outline-none hover:border-primary-light shadow-sm hover:shadow-md resize-vertical"
          :class="{
            'border-warning text-warning focus:ring-warning focus:border-warning bg-red-50': !descriptionIsValid && form.description.length > 0,
            'border-success focus:ring-success focus:border-success bg-green-50': descriptionIsValid && form.description.length > 0,
            'border-gray-300 text-anthracite focus:ring-primary focus:border-primary bg-white': form.description.length === 0
          }"
          placeholder="Describa el problema o solicitud en detalle... (mínimo 10 caracteres)"
          required
        />
        <div class="flex justify-between items-start mt-1">
          <p v-if="descriptionValidationMessage" class="text-warning text-xs italic">
            {{ descriptionValidationMessage }}
          </p>
          <div class="text-xs" :class="{
            'text-gray-400': form.description.length === 0,
            'text-success': descriptionIsValid && form.description.length > 0,
            'text-warning': !descriptionIsValid && form.description.length > 0
          }">
            <!-- contador visible -->
          </div>
        </div>
      </div>

      <!-- Estado y Prioridad -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Estado -->
        <div>
          <label for="status" class="block text-sm font-medium text-gray-700 mb-2">
            Estado *
          </label>
          <select
            id="status"
            v-model="form.status"
            class="block w-full px-4 py-3 text-base border rounded-xl font-sans transition-all duration-200 focus:outline-none hover:border-primary-light shadow-sm hover:shadow-md focus:ring-2 focus:ring-offset-1 focus:ring-opacity-50"
            :class="{
              'border-warning focus:ring-warning focus:border-warning bg-red-50': !statusIsValid && form.status !== '',
              'border-success focus:ring-success focus:border-success bg-green-50': statusIsValid && form.status !== '',
              'border-gray-300 text-anthracite focus:ring-primary focus:border-primary bg-white': form.status === ''
            }"
            required
          >
            <option value="">Seleccione un estado</option>
            <option
              v-for="status in ticketStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <p v-if="statusValidationMessage" class="text-warning text-xs italic mt-1">
            {{ statusValidationMessage }}
          </p>
        </div>

        <!-- Prioridad -->
        <div>
          <label for="priority" class="block text-sm font-medium text-gray-700 mb-2">
            Prioridad *
          </label>
          <select
            id="priority"
            v-model="form.priority"
            class="block w-full px-4 py-3 text-base border rounded-xl font-sans transition-all duration-200 focus:outline-none hover:border-primary-light shadow-sm hover:shadow-md focus:ring-2 focus:ring-offset-1 focus:ring-opacity-50"
            :class="{
              'border-warning focus:ring-warning focus:border-warning bg-red-50': !priorityIsValid && form.priority !== '',
              'border-success focus:ring-success focus:border-success bg-green-50': priorityIsValid && form.priority !== '',
              'border-gray-300 text-anthracite focus:ring-primary focus:border-primary bg-white': form.priority === ''
            }"
            required
          >
            <option value="">Seleccione una prioridad</option>
            <option
              v-for="priority in ticketPriorities"
              :key="priority.value"
              :value="priority.value"
            >
              {{ priority.label }}
            </option>
          </select>
          <p v-if="priorityValidationMessage" class="text-warning text-xs italic mt-1">
            {{ priorityValidationMessage }}
          </p>
        </div>
      </div>

      <!-- Categoría y Asignado a -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Categoría -->
        <div>
          <label for="category" class="block text-sm font-medium text-gray-700 mb-2">
            Categoría *
          </label>
          <InputField
            id="category"
            v-model="form.category"
            type="text"
            placeholder="Ej: Facturación, Acceso, Consulta"
            :is-valid="categoryIsValid"
            :validation-message="categoryValidationMessage"
            required
          />
        </div>

        <!-- Asignado a -->
        <div>
          <label for="assignedTo" class="block text-sm font-medium text-gray-700 mb-2">
            Asignado a
          </label>
          <select
            id="assignedTo"
            v-model="form.assignedToId"
            class="block w-full px-4 py-3 text-base border rounded-xl font-sans transition-all duration-200 focus:outline-none hover:border-primary-light shadow-sm hover:shadow-md border-gray-300 text-anthracite focus:ring-primary focus:border-primary bg-white focus:ring-2 focus:ring-offset-1 focus:ring-opacity-50"
          >
            <option value="">Sin asignar</option>
            <option
              v-for="member in teamMembers"
              :key="member.id"
              :value="member.id"
            >
              {{ member.name }} - {{ member.role }}
            </option>
          </select>
        </div>
      </div>

      <!-- Fechas -->
      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <!-- Fecha límite -->
        <div>
          <label for="dueDate" class="block text-sm font-medium text-gray-700 mb-2">
            Fecha Límite
          </label>
          <InputField
            id="dueDate"
            v-model="form.dueDate"
            type="datetime-local"
            :is-valid="dueDateIsValid"
            :validation-message="dueDateValidationMessage"
          />
        </div>

        <!-- SLA Percentage (solo lectura) -->
        <div>
          <label for="slaPercent" class="block text-sm font-medium text-gray-700 mb-2">
            Progreso SLA (%)
          </label>
          <InputField
            id="slaPercent"
            v-model="form.slaPercent"
            type="number"
            :disabled="true"
            input-class="bg-gray-100"
          />
        </div>
      </div>

      <!-- Campos Personalizados -->
      <div class="border-t border-gray-200 pt-6">
        <h3 class="text-lg font-medium text-gray-900 mb-4">Campos Personalizados</h3>
        
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <!-- Ambiente -->
          <div>
            <label for="environment" class="block text-sm font-medium text-gray-700 mb-2">
              Ambiente
            </label>
            <select
              id="environment"
              v-model="form.customFields.environment"
              class="block w-full px-4 py-3 text-base border rounded-xl font-sans transition-all duration-200 focus:outline-none hover:border-primary-light shadow-sm hover:shadow-md border-gray-300 text-anthracite focus:ring-primary focus:border-primary bg-white focus:ring-2 focus:ring-offset-1 focus:ring-opacity-50"
            >
              <option value="">Seleccione un ambiente</option>
              <option value="Desarrollo">Desarrollo</option>
              <option value="Testing">Testing</option>
              <option value="Staging">Staging</option>
              <option value="Producción">Producción</option>
            </select>
          </div>

          <!-- Usuarios Afectados -->
          <div>
            <label for="affectedUsers" class="block text-sm font-medium text-gray-700 mb-2">
              Usuarios Afectados
            </label>
            <InputField
              id="affectedUsers"
              v-model="form.customFields.affectedUsers"
              type="text"
              placeholder="Ej: 15 usuarios, Todos los clientes"
              :is-valid="affectedUsersIsValid"
              :validation-message="affectedUsersValidationMessage"
            />
          </div>

          <!-- Severidad -->
          <div>
            <label for="severity" class="block text-sm font-medium text-gray-700 mb-2">
              Severidad
            </label>
            <select
              id="severity"
              v-model="form.customFields.severity"
              class="block w-full px-4 py-3 text-base border rounded-xl font-sans transition-all duration-200 focus:outline-none hover:border-primary-light shadow-sm hover:shadow-md border-gray-300 text-anthracite focus:ring-primary focus:border-primary bg-white focus:ring-2 focus:ring-offset-1 focus:ring-opacity-50"
            >
              <option value="">Seleccione severidad</option>
              <option value="Baja">Baja</option>
              <option value="Media">Media</option>
              <option value="Alta">Alta</option>
              <option value="Crítica">Crítica</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Tags -->
      <div>
        <label for="tags" class="block text-sm font-medium text-gray-700 mb-2">
          Tags
        </label>
        <InputField
          id="tags"
          v-model="tagsInput"
          type="text"
          placeholder="Ingrese tags separados por comas (ej: crítico, facturación, sistema)"
          @input="handleTagsInput"
        />
        <div class="mt-2">
          <div v-if="form.tags.length > 0" class="flex flex-wrap gap-2">
            <span
              v-for="(tag, index) in form.tags"
              :key="index"
              class="px-3 py-1 text-sm rounded-full flex items-center gap-1"
              :class="{
                'bg-blue-100 text-blue-800': tagsIsValid,
                'bg-red-100 text-red-800': !tagsIsValid
              }"
            >
              #{{ tag }}
              <button
                type="button"
                @click="removeTag(index)"
                class="ml-1 hover:opacity-75 focus:outline-none"
                :class="{
                  'text-blue-600': tagsIsValid,
                  'text-red-600': !tagsIsValid
                }"
              >
                ×
              </button>
            </span>
          </div>
          <div class="flex justify-between items-center mt-1">
            <p v-if="tagsValidationMessage" class="text-warning text-xs italic">
              {{ tagsValidationMessage }}
            </p>
            <div class="text-xs" :class="{
              'text-gray-400': form.tags.length === 0,
              'text-success': tagsIsValid && form.tags.length > 0,
              'text-warning': !tagsIsValid && form.tags.length > 0
            }">
              {{ form.tags.length }}/10 tags
            </div>
          </div>
        </div>
      </div>
    </form>

    <!-- Footer con botones -->
    <template #footer>
      <BaseButton
        variant="secondary"
        @click="handleCancel"
        :disabled="isSaving"
      >
        Cancelar
      </BaseButton>
      
      <BaseButton
        variant="primary"
        @click="handleSave"
        :loading="isSaving"
        :disabled="!isFormValid"
      >
        {{ isSaving ? 'Guardando...' : 'Guardar Cambios' }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script>
import BaseModal from './BaseModal.vue';
import InputField from './InputField.vue';
import BaseButton from './BaseButton.vue';
// TODO: Reemplazar con servicios reales cuando estén disponibles
const ticketStatuses = [
  { value: 'abierto', label: 'Abierto', color: 'bg-red-100 text-red-800', badgeColor: 'bg-red-500' },
  { value: 'en_progreso', label: 'En Progreso', color: 'bg-yellow-100 text-yellow-800', badgeColor: 'bg-yellow-500' },
  { value: 'cerrado', label: 'Cerrado', color: 'bg-gray-100 text-gray-800', badgeColor: 'bg-gray-500' }
];

const ticketPriorities = [
  { value: 'baja', label: 'Baja', color: 'bg-green-500', textColor: 'text-green-700' },
  { value: 'media', label: 'Media', color: 'bg-yellow-500', textColor: 'text-yellow-700' },
  { value: 'alta', label: 'Alta', color: 'bg-red-500', textColor: 'text-red-700' }
];

const teamMembers = [
  { id: 201, name: 'Juan Pérez', email: 'juan.perez@ecomac.com', avatar: 'JP', role: 'Desarrollador Senior' },
  { id: 202, name: 'Ana Silva', email: 'ana.silva@ecomac.com', avatar: 'AS', role: 'Desarrolladora Frontend' },
  { id: 203, name: 'María López', email: 'maria.lopez@ecomac.com', avatar: 'ML', role: 'Especialista en Soporte' }
];
import TicketService from '../services/TicketService';
import notify from '../services/NotificationService.ts';
import { useToast } from 'vue-toastification';

export default {
  name: 'TicketEditModal',
  components: {
    BaseModal,
    InputField,
    BaseButton
  },
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    ticket: {
      type: Object,
      default: () => null
    }
  },
  emits: ['update:modelValue', 'save', 'cancel'],
  data() {
    return {
      isSaving: false,
      tagsInput: '',
      originalData: null,
      form: {
        title: '',
        description: '',
        status: '',
        priority: '',
        category: '',
        assignedToId: null,
        dueDate: '',
        slaPercent: 0,
        customFields: {
          environment: '',
          affectedUsers: '',
          severity: ''
        },
        tags: []
      }
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    ticketStatuses: () => ticketStatuses,
    ticketPriorities: () => ticketPriorities,
    teamMembers: () => teamMembers,
    
    // Validaciones del título
    titleIsValid() {
      const title = this.form.title?.trim() || '';
      return title.length >= 3 && title.length <= 200;
    },
    titleValidationMessage() {
      const title = this.form.title?.trim() || '';
      if (title.length === 0) {
        return 'El título es requerido';
      }
      if (title.length < 3) {
        return 'El título debe tener al menos 3 caracteres';
      }
      if (title.length > 200) {
        return 'El título no puede exceder 200 caracteres';
      }
      return '';
    },
    
    // Validaciones de la descripción
    descriptionIsValid() {
      const description = this.form.description?.trim() || '';
      return description.length >= 10 && description.length <= 2000;
    },
    descriptionValidationMessage() {
      const description = this.form.description?.trim() || '';
      if (description.length === 0) {
        return 'La descripción es requerida';
      }
      if (description.length < 10) {
        return 'La descripción debe tener al menos 10 caracteres';
      }
      if (description.length > 2000) {
        return 'La descripción no puede exceder 2000 caracteres';
      }
      return '';
    },
    
    // Validaciones del estado
    statusIsValid() {
      return this.form.status && this.form.status.trim().length > 0;
    },
    statusValidationMessage() {
      if (!this.statusIsValid) {
        return 'Debe seleccionar un estado';
      }
      return '';
    },
    
    // Validaciones de la prioridad
    priorityIsValid() {
      return this.form.priority && this.form.priority.trim().length > 0;
    },
    priorityValidationMessage() {
      if (!this.priorityIsValid) {
        return 'Debe seleccionar una prioridad';
      }
      return '';
    },
    
    // Validaciones de la categoría
    categoryIsValid() {
      const category = this.form.category?.trim() || '';
      return category.length >= 2 && category.length <= 100;
    },
    categoryValidationMessage() {
      const category = this.form.category?.trim() || '';
      if (category.length === 0) {
        return 'La categoría es requerida';
      }
      if (category.length < 2) {
        return 'La categoría debe tener al menos 2 caracteres';
      }
      if (category.length > 100) {
        return 'La categoría no puede exceder 100 caracteres';
      }
      return '';
    },
    
    // Validaciones de fecha límite (opcional pero con formato)
    dueDateIsValid() {
      if (!this.form.dueDate) return true; // Es opcional
      const selectedDate = new Date(this.form.dueDate);
      const now = new Date();
      const minDate = new Date(now.getTime() + (30 * 60 * 1000)); // 30 minutos desde ahora
      return selectedDate >= minDate;
    },
    dueDateValidationMessage() {
      if (!this.form.dueDate) return '';
      if (!this.dueDateIsValid) {
        return 'La fecha límite debe ser al menos 30 minutos en el futuro';
      }
      return '';
    },
    
    // Validaciones de usuarios afectados (opcional)
    affectedUsersIsValid() {
      if (!this.form.customFields.affectedUsers) return true; // Es opcional
      const users = this.form.customFields.affectedUsers.trim();
      return users.length <= 200;
    },
    affectedUsersValidationMessage() {
      if (!this.affectedUsersIsValid) {
        return 'Los usuarios afectados no pueden exceder 200 caracteres';
      }
      return '';
    },
    
    // Validaciones de tags
    tagsIsValid() {
      return this.form.tags.length <= 10 && this.form.tags.every(tag => tag.length <= 50);
    },
    tagsValidationMessage() {
      if (this.form.tags.length > 10) {
        return 'No puede tener más de 10 tags';
      }
      if (this.form.tags.some(tag => tag.length > 50)) {
        return 'Cada tag no puede exceder 50 caracteres';
      }
      return '';
    },
    
    // Validación general del formulario
    isFormValid() {
      return this.titleIsValid && 
             this.descriptionIsValid && 
             this.statusIsValid &&
             this.priorityIsValid &&
             this.categoryIsValid &&
             this.dueDateIsValid &&
             this.affectedUsersIsValid &&
             this.tagsIsValid;
    },
    
    // Mostrar errores de validación
    hasValidationErrors() {
      return !this.isFormValid;
    },
    
    // Lista de errores para mostrar al usuario
    validationErrors() {
      const errors = [];
      if (!this.titleIsValid) errors.push(this.titleValidationMessage);
      if (!this.descriptionIsValid) errors.push(this.descriptionValidationMessage);
      if (!this.statusIsValid) errors.push(this.statusValidationMessage);
      if (!this.priorityIsValid) errors.push(this.priorityValidationMessage);
      if (!this.categoryIsValid) errors.push(this.categoryValidationMessage);
      if (!this.dueDateIsValid) errors.push(this.dueDateValidationMessage);
      if (!this.affectedUsersIsValid) errors.push(this.affectedUsersValidationMessage);
      if (!this.tagsIsValid) errors.push(this.tagsValidationMessage);
      return errors.filter(error => error.length > 0);
    },
    
    hasUnsavedChanges() {
      if (!this.originalData) return false;
      return JSON.stringify(this.form) !== JSON.stringify(this.originalData);
    }
  },
  watch: {
    ticket: {
      handler(newTicket) {
        if (newTicket) {
          this.populateForm(newTicket);
        }
      },
      immediate: true,
      deep: true
    },
    modelValue(newVal) {
      if (newVal && this.ticket) {
        this.populateForm(this.ticket);
      }
    }
  },
  methods: {
    populateForm(ticketData) {
      // Crear una copia profunda para evitar mutaciones
      this.form = {
        title: ticketData.title || '',
        description: ticketData.description || '',
        status: ticketData.status || '',
        priority: ticketData.priority || '',
        category: ticketData.category || '',
        assignedToId: ticketData.assignedTo?.id || null,
        dueDate: this.formatDateForInput(ticketData.dueDate),
        slaPercent: ticketData.slaPercent || 0,
        customFields: {
          environment: ticketData.customFields?.environment || '',
          affectedUsers: ticketData.customFields?.affectedUsers || '',
          severity: ticketData.customFields?.severity || ''
        },
        tags: [...(ticketData.tags || [])]
      };

      // Actualizar el input de tags
      this.tagsInput = this.form.tags.join(', ');
      
      // Guardar estado original para detectar cambios
      this.originalData = JSON.parse(JSON.stringify(this.form));
    },
    formatDateForInput(dateString) {
      if (!dateString) return '';
      
      try {
        const date = new Date(dateString);
        // Formato para datetime-local input: yyyy-MM-ddTHH:mm
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${year}-${month}-${day}T${hours}:${minutes}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return '';
      }
    },
    handleTagsInput() {
      // Procesar tags cuando el usuario escribe
      const tags = this.tagsInput
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0)
        .filter((tag, index, arr) => arr.indexOf(tag) === index); // Remover duplicados
      
      this.form.tags = tags;
    },
    removeTag(index) {
      this.form.tags.splice(index, 1);
      this.tagsInput = this.form.tags.join(', ');
    },
    async handleSave() {
      // Validar formulario antes de guardar
      if (!this.isFormValid) {
        this.showValidationErrors();
        return;
      }

      this.isSaving = true;

      try {
        // Preparar datos para enviar al API
        const dataToUpdate = {
          ...this.ticket,
          ...this.form,
          // Convertir assignedToId de vuelta al objeto completo
          assignedTo: this.form.assignedToId 
            ? this.teamMembers.find(member => member.id === this.form.assignedToId)
            : null,
          // Convertir fecha de vuelta al formato ISO
          dueDate: this.form.dueDate ? new Date(this.form.dueDate).toISOString() : null
        };

        console.log('Guardando ticket con datos:', dataToUpdate);

        // Llamar al servicio de API para actualizar el ticket
        const updatedTicket = await TicketService.updateTicket(this.ticket.id, dataToUpdate);
        
        console.log('Ticket actualizado exitosamente:', updatedTicket);

        // Emitir evento con los datos actualizados del servidor
        this.$emit('save', updatedTicket);
        
        // Mostrar notificación de éxito
        this.showSuccessNotification();
        
        // Cerrar modal
        this.isVisible = false;
        
        // Reset form después de un breve delay
        setTimeout(() => {
          this.resetForm();
        }, 300);
        
      } catch (error) {
        console.error('Error saving ticket:', error);
        
        // Manejar diferentes tipos de errores
        let errorMessage = 'Error al guardar el ticket';
        
        if (error.response) {
          // Error de respuesta HTTP
          switch (error.response.status) {
            case 400:
              errorMessage = 'Datos inválidos. Por favor revise los campos.';
              break;
            case 401:
              errorMessage = 'No tiene autorización para realizar esta acción.';
              break;
            case 403:
              errorMessage = 'No tiene permisos para editar este ticket.';
              break;
            case 404:
              errorMessage = 'El ticket no existe o ha sido eliminado.';
              break;
            case 409:
              errorMessage = 'El ticket ha sido modificado por otro usuario. Por favor recargue la página.';
              break;
            case 500:
              errorMessage = 'Error interno del servidor. Por favor intente más tarde.';
              break;
            default:
              errorMessage = `Error del servidor: ${error.response.status}`;
          }
        } else if (error.request) {
          // Error de red
          errorMessage = 'Error de conexión. Por favor verifique su conexión a internet.';
        } else if (error.message) {
          // Otro tipo de error
          errorMessage = error.message;
        }
        
        this.showErrorNotification(errorMessage);
      } finally {
        this.isSaving = false;
      }
    },
    handleCancel() {
      // Confirmar si hay cambios no guardados
      if (!this.confirmCancel()) {
        return;
      }
      
      this.$emit('cancel');
      this.isVisible = false;
      
      // Reset form después de un breve delay
      setTimeout(() => {
        this.resetForm();
      }, 300);
    },
    handleClose() {
      // Si hay cambios no guardados, confirmar antes de cerrar
      if (this.hasUnsavedChanges) {
        const confirmClose = confirm('¿Está seguro de que desea cerrar? Los cambios no guardados se perderán.');
        if (!confirmClose) {
          return;
        }
      }
      
      this.handleCancel();
    },
    resetForm() {
      this.form = {
        title: '',
        description: '',
        status: '',
        priority: '',
        category: '',
        assignedToId: null,
        dueDate: '',
        slaPercent: 0,
        customFields: {
          environment: '',
          affectedUsers: '',
          severity: ''
        },
        tags: []
      };
      this.tagsInput = '';
      this.originalData = null;
    },
    
    /**
     * Muestra los errores de validación al usuario
     */
    showValidationErrors() {
      const toast = useToast();
      const errors = this.validationErrors;
      
      if (errors.length > 0) {
        // Mostrar el primer error como notificación principal
        toast.error(`Error de validación: ${errors[0]}`, {
          position: 'top-right',
          timeout: 5000,
          closeOnClick: true,
          pauseOnFocusLoss: false,
          pauseOnHover: true,
          draggable: true,
          showCloseButtonOnHover: false,
          hideProgressBar: false,
          closeButton: "button",
          icon: true,
          rtl: false
        });
        
        // Si hay múltiples errores, mostrar un resumen
        if (errors.length > 1) {
          const additionalErrors = errors.slice(1, 3); // Máximo 3 errores adicionales
          const remainingCount = errors.length - 3;
          
          let summary = `Errores adicionales:\n${additionalErrors.join('\n')}`;
          if (remainingCount > 0) {
            summary += `\n... y ${remainingCount} error(es) más`;
          }
          
          setTimeout(() => {
            toast.warning(summary, {
              position: 'top-right',
              timeout: 7000,
              closeOnClick: true,
              pauseOnFocusLoss: false,
              pauseOnHover: true,
              draggable: true,
              showCloseButtonOnHover: false,
              hideProgressBar: false,
              closeButton: "button",
              icon: true,
              rtl: false
            });
          }, 500);
        }
      }
    },
    
    /**
     * Muestra notificación de éxito
     */
    showSuccessNotification() {
      notify.ticketUpdated();
    },
    
    /**
     * Muestra notificación de error
     * @param {string} message - Mensaje de error personalizado
     */
    showErrorNotification(message = 'Error al guardar el ticket') {
      notify.error(message);
    },
    
    /**
     * Confirma si el usuario quiere cancelar con cambios no guardados
     * @returns {boolean} true si debe continuar con la cancelación
     */
    confirmCancel() {
      if (!this.hasUnsavedChanges) {
        return true;
      }
      
      return confirm(
        '¿Está seguro de que desea cancelar?\n\n' +
        'Los cambios realizados se perderán si no los guarda antes de continuar.\n\n' +
        'Presione "Aceptar" para continuar sin guardar, o "Cancelar" para volver al formulario.'
      );
    }
  }
};
</script>

<style scoped>
/* Estilos específicos para el formulario */
.space-y-6 > * + * {
  margin-top: 1.5rem;
}

/* Estilos para textarea */
textarea {
  min-height: 100px;
}

/* Estilos para los tags */
.tag-item {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Estilos para campos requeridos */
label[for]:after {
  content: "";
}

label:has(+ input[required]):after,
label:has(+ select[required]):after,
label:has(+ textarea[required]):after {
  content: " *";
  color: #ef4444;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .grid-cols-1.md\:grid-cols-2 {
    grid-template-columns: 1fr;
  }
  
  .grid-cols-1.md\:grid-cols-3 {
    grid-template-columns: 1fr;
  }
}
</style>
